import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../lib/adminApi';
import type { ApiAdminPayment } from '../../types/admin';
import { LoadingSpinner } from '../common/Loading';
import { ErrorMessage } from '../common/Error';
import { toast } from 'sonner';
import { cn } from '../../utils/cn';

// Espelho do catálogo server-side (server/src/lib/packages.ts) — a API de admin só
// devolve productId, então a tradução para nome amigável vive aqui.
const CREDIT_PACKAGE_NAMES: Record<string, string> = {
  basic: 'Básico (100 créditos)',
  plus: 'Plus (300 créditos)',
  premium: 'Premium (500 créditos)',
  consumo: 'Pré-Pago',
};

const STORE_PRODUCT_NAMES: Record<string, string> = {
  'mapa-completo': 'Mapa Astral Completo',
  sinastria: 'Sinastria (Duas Cartas)',
  relocacao: 'Relatório de Relocação',
  previsao: 'Previsões Anuais',
};

const productLabel = (payment: ApiAdminPayment): string => {
  const table = payment.kind === 'credit_package' ? CREDIT_PACKAGE_NAMES : STORE_PRODUCT_NAMES;
  return table[payment.productId] ?? payment.productId;
};

const kindLabel = (payment: ApiAdminPayment): string =>
  payment.kind === 'credit_package'
    ? `${payment.credits} créditos`
    : 'Produto da loja';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_STYLES: Record<ApiAdminPayment['status'], { label: string; badge: string }> = {
  pending: { label: 'pendente', badge: 'bg-yellow-500/20 text-yellow-300' },
  approved: { label: 'aprovado', badge: 'bg-green-500/20 text-green-300' },
  cancelled: { label: 'cancelado', badge: 'bg-white/10 text-purple-200/70' },
  refunded: { label: 'reembolsado', badge: 'bg-red-500/20 text-red-300' },
};

export const AdminPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<ApiAdminPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { payments: list } = await adminApi.listPayments();
      setPayments(list);
    } catch (err) {
      console.error('AdminPayments: erro ao listar pagamentos', err);
      setError('Erro ao carregar pagamentos');
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-purple-200">Pagamentos</h2>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <ErrorMessage message={error} />
      ) : payments.length === 0 ? (
        <p className="text-purple-200/70 py-4 text-center">Nenhum pagamento registrado</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-purple-200/60">
                <th className="py-2 pr-4 font-medium">Data</th>
                <th className="py-2 pr-4 font-medium">Usuário</th>
                <th className="py-2 pr-4 font-medium">Produto</th>
                <th className="py-2 pr-4 font-medium">Tipo</th>
                <th className="py-2 pr-4 font-medium text-right">Valor</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium">Fila</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => {
                const status = STATUS_STYLES[payment.status] ?? STATUS_STYLES.cancelled;
                const needsDelivery =
                  payment.kind === 'store_product' && payment.status === 'approved';
                return (
                  <tr
                    key={payment.id}
                    className="border-t border-white/10 text-purple-200 align-top"
                  >
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {new Date(payment.createdAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="py-3 pr-4 min-w-[12rem]">
                      <p className="truncate">{payment.userName}</p>
                      <p className="text-xs text-purple-200/60 truncate">{payment.userEmail}</p>
                    </td>
                    <td className="py-3 pr-4">{productLabel(payment)}</td>
                    <td className="py-3 pr-4 text-purple-200/80">{kindLabel(payment)}</td>
                    <td className="py-3 pr-4 text-right font-medium whitespace-nowrap">
                      {brl.format(payment.amount)}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-full whitespace-nowrap',
                          status.badge,
                        )}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="py-3">
                      {needsDelivery ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 whitespace-nowrap">
                          entregar
                        </span>
                      ) : (
                        <span className="text-purple-200/30">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};